import { css } from '@emotion/react'
import { ExternalStyles, Text, Theme, Tooltip, useTheme, VFlow } from 'bold-ui'
import { Box } from 'components/Box'
import { RadioGroupField, RadioGroupFieldProps } from 'components/form'
import { RequiredLabel } from 'components/form/core/RequiredLabel/RequiredLabel'

interface RadioGroupBoxFieldProps<T extends string | number | string[]> extends RadioGroupFieldProps<T> {
  tooltipText?: string
  labelStyle?: ExternalStyles
  boxStyle?: ExternalStyles
}

export function RadioGroupBoxField<T extends string | number | string[]>(props: Readonly<RadioGroupBoxFieldProps<T>>) {
  const {
    label,
    options,
    required,
    disabled = false,
    tooltipText,
    labelStyle,
    boxStyle,
    lines,
    vSpacing = 0.5,
    ...rest
  } = props

  const theme = useTheme()
  const styles = createStyles(theme, disabled)

  return (
    <Tooltip text={tooltipText}>
      <Box style={[styles.box, boxStyle]}>
        <VFlow vSpacing={vSpacing}>
          {required ? (
            <RequiredLabel
              color={disabled ? 'disabled' : 'normal'}
              fontWeight='bold'
              label={label}
              style={labelStyle}
            />
          ) : (
            <Text color={disabled ? 'disabled' : 'normal'} fontWeight='bold' style={labelStyle}>
              {label}
            </Text>
          )}
          <RadioGroupField<T>
            options={options}
            required={required}
            lines={lines ?? options.length}
            disabled={disabled}
            {...rest}
          />
        </VFlow>
      </Box>
    </Tooltip>
  )
}

const createStyles = (theme: Theme, isDisabled: boolean) => ({
  box: css`
    border-color: ${theme.pallete.gray.c80};
    background: ${isDisabled ? theme.pallete.gray.c90 : theme.pallete.surface.main};
  `,
})
